import React, { useState } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { useKnowledgeSmartCardAnalytics } from '../../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import {
	CardVariant,
	KnowledgeSmartCard,
} from '../../../common/ui/knowledge-smart-card/knowledge-smart-card';
import { SourcesStack } from '../../../common/ui/sources-stack/sources-stack';

const messages = defineMessages({
	sources_text: {
		id: 'search_ai.dynamic_layout_sources_text',
		defaultMessage: '{numSources, plural, one {Source} other {Sources}}',
		description: 'Sources text for dynamic layout dropdown',
	},
	sources_group_title: {
		id: 'search_ai.dynamic_layout_sources_group_title',
		defaultMessage:
			'{numSources, plural, one {{numSources, number} Source} other {{numSources, number} Sources}}',
		description: 'Sources group title in dropdown showing the count',
	},
});

const styles = cssMap({
	dropdownContent: {
		maxWidth: '400px',
	},
});

export const DynamicLayoutSourcesDropdown = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const context = useAIAnswerContext();
	const { commonAttributes } = context;
	const { sources } = commonAttributes;
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	if (!sources || sources.length === 0) {
		return null;
	}
	const numSources = sources.length;

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Button ref={triggerRef} {...props}>
					<Inline space="space.050" alignBlock="center">
						<SourcesStack sources={sources} />
						<Text color="color.text.subtle">
							{formatMessage(messages.sources_text, { numSources })}
						</Text>
					</Inline>
				</Button>
			)}
			isOpen={isOpen}
			onOpenChange={(attrs) => setIsOpen(attrs.isOpen)}
		>
			<Box xcss={styles.dropdownContent}>
				<DropdownItemGroup title={formatMessage(messages.sources_group_title, { numSources })}>
					{sources.map((source, index) => (
						<DropdownItem key={source.url || index}>
							<KnowledgeSmartCard
								source={source}
								variant={CardVariant.DROPDOWN_SOURCE}
								index={index}
								searchSessionId={searchSessionId}
								onClickHandler={handleSmartCardClick}
							/>
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</Box>
		</DropdownMenu>
	);
};
